import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAnalytics } from "../lib/api";
import { usePublicHub } from "../hooks/usePublicHub";
import { PHOTO_AVATARS } from "./AvatarChip";
import { GamePickStrip } from "./landing/GamePickStrip";
import { LiveStreamersTicker } from "./LiveStreamersTicker";

/**
 * Marketing landing — Iteration 76.
 *
 * Brief (per the user):
 *   1. Match the rest of the site's font (Press Start 2P pixel for
 *      headlines via `.font-display` + Inter for body) — i.e. drop
 *      the Space Grotesk swap-out introduced in iteration 70/71.
 *   2. Way more compact. One feature section. Drop the photo of
 *      How-it-works, drop the hero photo, drop the marketing
 *      photography entirely. If we keep any visuals, they're the
 *      LEAGUE HUB operative portraits (`PHOTO_AVATARS` in
 *      `AvatarChip.js`) — never stock photos.
 *   3. Live counter signal preserved (Iteration 72 work).
 *   4. Pricing stays on its own `/pricing` page (Iteration 71 rule).
 *
 * The earlier landing's `body[data-landing="true"]` reset that
 * stripped CRT scanlines + text-shadow is REMOVED so the landing
 * now inherits the same neon look as PlayerDashboard / Teams /
 * Leagues — no more visual seam at sign-in.
 */
export default function Hero({ onSignIn }) {
  const nav = useNavigate();
  const handleStartFree = () => {
    if (typeof onSignIn === "function") onSignIn();
  };
  const handleBrowse = () => nav("/leagues");

  return (
    <div data-testid="landing-root" className="page-z">
      <HeroBlock
        onStartFree={handleStartFree}
        onBrowse={handleBrowse}
      />
      <section
        id="games"
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-12"
        data-testid="landing-games"
      >
        <LiveStreamersTicker />
        <GamePickStrip />
      </section>
      <FeaturesBlock />
      <FinalCta onStartFree={handleStartFree} onSeePricing={() => nav("/pricing")} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero — single screen, no photos, pixel headline.
// ---------------------------------------------------------------------------
function HeroBlock({ onStartFree, onBrowse }) {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState({ players: 0, leagues: 0 });

  useEffect(() => {
    fetchAnalytics()
      .then((d) => setStats(d?.totals || null))
      .catch(() => setStats(null));
  }, []);

  // Live signal — re-key the affected stat so the pulse re-runs.
  const onHubEvent = useCallback((evt) => {
    if (evt?.type !== "stats:bump" || !evt.totals) return;
    setStats((cur) => ({ ...(cur || {}), ...evt.totals }));
    if (evt.scope === "players" || evt.scope === "leagues") {
      setPulse((p) => ({ ...p, [evt.scope]: p[evt.scope] + 1 }));
    }
  }, []);
  usePublicHub(onHubEvent, true);

  return (
    <section
      id="hero"
      data-testid="landing-hero"
      className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 sm:pt-20 pb-10 text-center"
    >
      <img
        src="/assets/branding/leaguehub-banner.png"
        alt="LEAGUE HUB — cyberpunk operatives in hoods"
        data-testid="hero-banner"
        loading="eager"
        className="block mx-auto rounded-2xl w-full max-w-3xl mb-10 border border-white/10 shadow-[0_20px_60px_rgba(20,184,166,0.10)]"
      />
      <p
        data-testid="hero-eyebrow"
        className="text-[11px] tracking-[0.2em] text-[#14b8a6] font-bold"
      >
        ESPORTS LEAGUE OPERATIONS
      </p>
      <h1
        data-testid="hero-title"
        className="font-display grad-text mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.2]"
      >
        RUN YOUR LEAGUE.
        <br className="hidden sm:block" />
        FREE TO START.
      </h1>
      <p
        data-testid="hero-subcopy"
        className="mt-5 text-white/70 text-[14px] sm:text-[15px] max-w-xl mx-auto leading-relaxed"
      >
        Brackets, MMR, rosters, and Discord-first sign-in — built
        for community admins and small orgs.
      </p>
      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onStartFree}
          data-testid="hero-cta-primary"
          className="btn-neon px-6 py-3 rounded-xl text-[11px] tracking-widest font-bold"
        >
          CREATE YOUR LEAGUE — FREE
        </button>
        <button
          type="button"
          onClick={onBrowse}
          data-testid="hero-cta-secondary"
          className="btn-ghost px-6 py-3 rounded-xl text-[11px] tracking-widest font-bold"
        >
          BROWSE LIVE LEAGUES
        </button>
      </div>

      <LiveStatsRow stats={stats} pulse={pulse} />
    </section>
  );
}

function LiveStatsRow({ stats, pulse }) {
  const fallback = [
    { v: "FREE",    l: "TO START A LEAGUE" },
    { v: "DISCORD", l: "FIRST SIGN-IN" },
    { v: "OPEN",    l: "BETA · JOIN US EARLY" },
  ];
  const live =
    stats && stats.players != null && stats.leagues != null
      ? [
          { v: stats.players.toLocaleString(), l: "OPERATORS SIGNED UP", pk: pulse.players },
          { v: stats.leagues.toLocaleString(), l: "LEAGUES ACTIVE",       pk: pulse.leagues },
          { v: "\u221E",                          l: "GAMES SUPPORTED",     pk: 0 },
        ]
      : fallback.map((r) => ({ ...r, pk: 0 }));
  return (
    <dl
      data-testid="hero-stats"
      className="mt-9 max-w-md mx-auto grid grid-cols-3 gap-3"
    >
      {live.map((s, i) => (
        <div
          key={i}
          data-testid={`hero-stat-${i + 1}`}
          className="glass rounded-xl px-3 py-3 border border-white/10"
        >
          <dt
            key={`${i}-${s.pk}`}
            data-pulse={s.pk > 0 ? "true" : undefined}
            className="font-display grad-text text-[13px] sm:text-[15px] hero-stat-value"
          >
            {s.v}
          </dt>
          <dd className="text-[9px] tracking-widest text-white/45 mt-1 leading-tight">
            {s.l}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Features — single compact section, no images, pixel headline.
// ---------------------------------------------------------------------------
const FEATURES = [
  {
    title: "BRACKETS",
    body:  "Single, double, or round-robin. Match cards auto-advance once both captains confirm the score.",
  },
  {
    title: "MMR + TIERS",
    body:  "Glicko-2 ratings + skill tiers. Players see their rank, admins get a balanced leaderboard.",
  },
  {
    title: "DISCORD-FIRST",
    body:  "Sign-in with Discord, ping your team, share clips, run free-agent matchmaking — no app-hopping.",
  },
];

function FeaturesBlock() {
  return (
    <section
      id="features"
      data-testid="landing-features"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-14"
    >
      <header className="text-center max-w-xl mx-auto">
        <p className="text-[11px] tracking-[0.2em] text-[#14b8a6] font-bold">
          WHAT YOU GET
        </p>
        <h2 className="font-display grad-text mt-3 text-2xl sm:text-3xl">
          EVERYTHING NEEDED
          <br />
          TO RUN A SEASON.
        </h2>
      </header>
      <div className="mt-9 grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {FEATURES.map((f, i) => (
          <article
            key={f.title}
            data-testid={`landing-feature-${i + 1}`}
            className="glass rounded-2xl p-5 border border-white/10"
          >
            <p className="font-display grad-text text-[12px] sm:text-[13px]">
              {f.title}
            </p>
            <p className="mt-3 text-[12.5px] text-white/65 leading-relaxed">
              {f.body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final CTA — invitation, pricing stays on its dedicated /pricing route.
// ---------------------------------------------------------------------------
function FinalCta({ onStartFree, onSeePricing }) {
  return (
    <section
      data-testid="landing-final-cta"
      className="max-w-3xl mx-auto px-6 lg:px-10 pt-2 pb-20 text-center"
    >
      <div className="glass rounded-3xl p-8 border border-[#14b8a6]/30">
        <p className="text-[11px] tracking-[0.2em] text-[#14b8a6] font-bold">
          FREE FOREVER FOR PLAYERS
        </p>
        <h2 className="font-display grad-text mt-3 text-2xl sm:text-3xl leading-[1.25]">
          FIRST MATCH IN
          <br />
          FIVE MINUTES.
        </h2>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onStartFree}
            data-testid="landing-final-cta-button"
            className="btn-neon px-6 py-3 rounded-xl text-[11px] tracking-widest font-bold"
          >
            START FREE
          </button>
          <button
            type="button"
            onClick={onSeePricing}
            data-testid="landing-see-pricing"
            className="btn-ghost px-6 py-3 rounded-xl text-[11px] tracking-widest font-bold"
          >
            SEE PRICING
          </button>
        </div>
      </div>
    </section>
  );
}

// Keep PHOTO_AVATARS importable from this file in case future variants
// want to add a tasteful operative-only decorative row above the
// final CTA. Re-exported here so callers can `import { PHOTO_AVATARS }
// from "./Hero"` without reaching into the AvatarChip internals.
export { PHOTO_AVATARS };
